import { useCallback, useEffect, useRef } from 'react';
import { createLogger } from '../services/Debug';
export function useStatePersistence({
  transcript,
  annexText,
  isFakeRecording,
  selectedReportType,
  isRecording,
  highlightReportType,
  queueRef,
  setTranscript,
  setAnnexText,
  setisFakeRecording,
  setSelectedReportType,
  setHighlightReportType,
  setQueue,
  setQueueWithMetadata,
  queueMetadataRef,
  processQueue,
  processingRef,
  saveOrUpdateDraft  // Changed from autoSaveOrUpdateDraft
}) {
  // Use a ref to track if we're actively clearing to prevent auto-save
  const isClearingRef = useRef(false);
  const lastSavedRef = useRef({ transcript: '', annexText: '', reportType: '' });
  const log = createLogger('StatePersistence');
  const saveTemporaryState = useCallback(() => {
    // Don't save if we're actively clearing
    if (isClearingRef.current) {
      
      return;
    }

    if (isRecording || transcript || annexText) {

      localStorage.setItem('tempDictationState', JSON.stringify({
        transcript,
        annexText,
        isRecording: isFakeRecording,
        reportType: selectedReportType,
        queue: queueRef.current,
        highlightReportType: highlightReportType,
        timestamp: Date.now()
      }));
    }
  }, [transcript, annexText, isFakeRecording, selectedReportType, isRecording, highlightReportType, queueRef]);

  const clearTemporaryState = useCallback(() => {
    
    localStorage.removeItem('tempDictationState');
  }, []);

  const setIsClearing = useCallback((value) => {
    isClearingRef.current = value;
  }, []);

  // We no longer auto-save drafts to server on visibility/blur events
  // This function is kept for potential manual save scenarios
  const saveDraftIfNeeded = useCallback(async () => {
    if (isClearingRef.current) return;
    
    const hasContent = transcript?.trim() || annexText?.trim();
    const hasChanged = 
      lastSavedRef.current.transcript !== transcript ||
      lastSavedRef.current.annexText !== annexText ||
      lastSavedRef.current.reportType !== selectedReportType;
    
    if (hasContent && hasChanged) {
      
      try {
        await saveOrUpdateDraft(transcript, annexText, selectedReportType);
        // Update last saved state
        lastSavedRef.current = { transcript, annexText, reportType: selectedReportType };
      } catch (error) {
        log.error('Failed to save draft:', error);
      }
    }
  }, [transcript, annexText, selectedReportType, saveOrUpdateDraft]);

  // Effect for handling various exit/hide scenarios
  useEffect(() => {
    // Handle page visibility change (tab switch, minimize, lock screen)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !isClearingRef.current) {
        saveTemporaryState();
        // No longer auto-save draft to server on visibility change
      }
    };

    // Handle app losing focus
    const handleBlur = () => {
      if (!isClearingRef.current) {
        saveTemporaryState();
        // No longer auto-save draft to server on blur
      }
    };

    // Handle page unload (closing tab/window)
    const handleBeforeUnload = (e) => {
      if (!isClearingRef.current && (transcript?.trim() || annexText?.trim())) {
        saveTemporaryState();
        // Note: Can't use async operations in beforeunload
        // The draft save will be handled by the visibility change event
        
        // Show browser confirmation if there's unsaved content
        e.preventDefault();
        e.returnValue = '';
      }
    };

    // Handle app going to background (mobile)
    const handlePause = () => {
      if (!isClearingRef.current) {
        saveTemporaryState();
        // No longer auto-save draft to server on pause
      }
    };

    // Add all event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Mobile app events (if using Capacitor)
    if (window.Capacitor) {
      window.addEventListener('pause', handlePause);
    }

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      if (window.Capacitor) {
        window.removeEventListener('pause', handlePause);
      }
      
      // Save temporary state on unmount if not clearing
      if (!isClearingRef.current) {
        saveTemporaryState();
        // No longer auto-save draft to server on unmount
      }
    };
  }, [saveTemporaryState, saveDraftIfNeeded]);

  // Effect to restore state on mount
  useEffect(() => {
    const tempState = localStorage.getItem('tempDictationState');
    if (tempState) {
      try {
        const state = JSON.parse(tempState);
        
        // Restore if state is recent (less than 1 hour)
        if (Date.now() - state.timestamp < 3600000) {
          
          setTranscript(state.transcript || '');
          setAnnexText(state.annexText || '');
          
          // Update last saved ref to prevent immediate re-save
          lastSavedRef.current = {
            transcript: state.transcript || '',
            annexText: state.annexText || '',
            reportType: state.reportType || ''
          };

          // Don't auto-resume recording
          setisFakeRecording(false);
          setSelectedReportType(state.reportType || '');
          setHighlightReportType(state.highlightReportType || false);
          
          // Restore queue if exists
          if (state.queue && state.queue.length > 0) {
            setQueue(state.queue);
            queueRef.current = state.queue;
            
            // Recreate metadata for restored items
            const restoredMetadata = state.queue.map((_, index) => ({
              id: Date.now().toString() + index,
              audio: state.queue[index],
              status: 'pending',
              retries: 0,
              model: 'gpt-4o-transcribe',
              timestamp: Date.now()
            }));
            setQueueWithMetadata(restoredMetadata);
            queueMetadataRef.current = restoredMetadata;
            
            // Restart queue processing if needed
            if (!processingRef.current) {
              processQueue(state.queue);
            }
          }
        } else {
          // If state is too old, save as draft and clear
          if (state.transcript || state.annexText) {
              
            saveOrUpdateDraft(
              state.transcript || '', 
              state.annexText || '', 
              state.reportType || 'texte'
            );
          }
          clearTemporaryState();
        }
      } catch (error) {
        log.error('Error restoring state:', error);
        clearTemporaryState();
      }
    }
  }, []); // Only on mount

  return {
    saveTemporaryState,
    clearTemporaryState,
    setIsClearing,
    saveDraftIfNeeded
  };
}